import { NEXT_AVAILABLE_DATE_WEEKS } from 'types/OnlineBooking';

export const buildWalkingEligibilityRequestKey = (
    marketplaceId: number,
    addressId: number | undefined,
    numberOfPets: number
): string => `${marketplaceId}:${addressId ?? ''}:${numberOfPets}`;

export const buildWalkingStaffAvailabilityRequestKey = (
    marketplaceId: number,
    addressId: number | undefined,
    numberOfPets: number,
    serviceId: number,
    staffIds: Array<number>,
    startDate: string,
    weeks: number
): string => {
    const sortedStaffIds = [...staffIds].sort((a, b) => a - b).join(',');

    return `${buildWalkingEligibilityRequestKey(
        marketplaceId,
        addressId,
        numberOfPets
    )}:${serviceId}:${sortedStaffIds}:${startDate}:${weeks}`;
};

export const buildWalkingAvailabilityRequestKey = (
    marketplaceId: number,
    addressId: number | undefined,
    numberOfPets: number,
    serviceId: number,
    staffId: number | undefined,
    maxTimeframeWeeks: number
): string =>
    `${buildWalkingEligibilityRequestKey(marketplaceId, addressId, numberOfPets)}:${serviceId}:${
        staffId ?? ''
    }:${maxTimeframeWeeks}`;

// `booking_availability` answers with every day from `start_date` to the lead-time limit, so a
// response fetched from the earliest bookable day already covers the months the parent can browse.
export const walkingAvailabilityCovers = (
    availability: { startDate: string; endDate: string } | undefined,
    date: string
): boolean =>
    availability != null && date >= availability.startDate && date <= availability.endDate;

export const serializeWalkingRequestParams = (params: Record<string, unknown>): string => {
    const parts: Array<string> = [];

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            // Pin the bracket form against an axios upgrade (axios 0.24 default already emits []).
            value.forEach(item => {
                parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`);
            });
            return;
        }

        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    });

    return parts.join('&');
};

export const leadTimeToWeeks = (limit: string | null): number => {
    if (!limit) {
        return NEXT_AVAILABLE_DATE_WEEKS;
    }

    const match = /^(\d+)_(days|weeks|months)$/.exec(limit);

    if (!match) {
        return NEXT_AVAILABLE_DATE_WEEKS;
    }

    const n = Number(match[1]);

    if (!Number.isFinite(n) || n < 0) {
        return NEXT_AVAILABLE_DATE_WEEKS;
    }

    if (match[2] === 'days') {
        return Math.ceil(n / 7);
    }

    if (match[2] === 'weeks') {
        return n;
    }

    return Math.ceil((n * 30) / 7);
};

export const intersectWalkingStaffIds = (staffIdSets: Array<Array<number>>): Array<number> => {
    if (staffIdSets.length === 0) {
        return [];
    }

    return staffIdSets.reduce((acc, ids) => acc.filter(id => ids.includes(id)));
};

export interface WalkingStaffAvailabilityCall {
    serviceId: number;
    staffIds: Array<number>;
    addressId: number | undefined;
    numberOfPets: number;
    startDate: string;
    weeks: number;
}

export const deriveWalkingInitialSelectedDate = (
    existingDate: string | undefined,
    nextAvailableDates: Array<string | null>,
    earliestDate: string
): string => {
    if (existingDate) {
        return existingDate;
    }

    const present = nextAvailableDates.filter((date): date is string => date != null);

    if (present.length === 0) {
        return earliestDate;
    }

    const latest = present.reduce((furthest, date) => (date > furthest ? date : furthest));

    return latest < earliestDate ? earliestDate : latest;
};

export const deriveWalkingMaxDate = (
    responses: Array<{ endDate: string }>,
    fallback: string
): string => {
    if (responses.length === 0) {
        return fallback;
    }

    return responses
        .map(response => response.endDate)
        .reduce((earliest, date) => (date < earliest ? date : earliest));
};

export const walkingPetsForService = (
    items: Array<{ service?: { id: number } }>,
    serviceId: number
): number => items.filter(item => item.service?.id === serviceId).length;

export const distinctWalkingServiceIds = (
    items: Array<{ service?: { id: number } }>
): Array<number> => {
    const seen = new Set<number>();
    const ids: Array<number> = [];

    items.forEach(item => {
        const id = item.service?.id;

        if (id == null || seen.has(id)) {
            return;
        }

        seen.add(id);
        ids.push(id);
    });

    return ids;
};

const walkingAvailabilityKeysToInvalidate = (
    cacheKeys: Array<string>,
    params: {
        marketplaceId: number;
        addressId: number | undefined;
        numberOfPets: number;
        serviceId: number;
        staffId: number | undefined;
    }
): Array<string> => {
    const prefix = `${buildWalkingEligibilityRequestKey(
        params.marketplaceId,
        params.addressId,
        params.numberOfPets
    )}:${params.serviceId}:${params.staffId ?? ''}:`;

    return cacheKeys.filter(key => key.startsWith(prefix));
};

export const walkingAvailabilityKeysForItems = (
    cacheKeys: Array<string>,
    items: Array<{ service?: { id: number } }>,
    marketplaceId: number,
    addressId: number | undefined,
    staffId: number | undefined
): Array<string> => {
    const keys = new Set<string>();

    items.forEach(item => {
        if (!item.service) {
            return;
        }

        walkingAvailabilityKeysToInvalidate(cacheKeys, {
            marketplaceId,
            addressId,
            numberOfPets: walkingPetsForService(items, item.service.id),
            serviceId: item.service.id,
            staffId
        }).forEach(key => keys.add(key));
    });

    return Array.from(keys);
};

export const walkingStaffAvailabilityKeysForItems = (
    cacheKeys: Array<string>,
    items: Array<{ service?: { id: number } }>,
    marketplaceId: number,
    addressId: number | undefined
): Array<string> => {
    const keys = new Set<string>();

    items.forEach(item => {
        if (!item.service) {
            return;
        }

        const prefix = `${buildWalkingEligibilityRequestKey(
            marketplaceId,
            addressId,
            walkingPetsForService(items, item.service.id)
        )}:${item.service.id}:`;

        cacheKeys.filter(key => key.startsWith(prefix)).forEach(key => keys.add(key));
    });

    return Array.from(keys);
};

export const buildWalkingStaffAvailabilityCalls = (input: {
    items: Array<{ serviceId: number; staffIds: Array<number> }>;
    knownStaffIds: Array<number>;
    addressId: number | undefined;
    startDate: string;
    weeks: number;
}): Array<WalkingStaffAvailabilityCall> => {
    const intersection = intersectWalkingStaffIds(input.items.map(item => item.staffIds));
    const known = new Set(input.knownStaffIds);
    const staffIds: Array<number> = [];

    intersection.forEach(id => {
        if (known.has(id)) {
            staffIds.push(id);
            return;
        }

        console.warn('Walking staff availability skipped unknown staff id', id);
    });

    const seen = new Set<number>();
    const calls: Array<WalkingStaffAvailabilityCall> = [];

    input.items.forEach(item => {
        if (seen.has(item.serviceId)) {
            return;
        }

        seen.add(item.serviceId);
        calls.push({
            serviceId: item.serviceId,
            staffIds,
            addressId: input.addressId,
            numberOfPets: input.items.filter(entry => entry.serviceId === item.serviceId).length,
            startDate: input.startDate,
            weeks: input.weeks
        });
    });

    return calls;
};
